import { Panel } from '../../../layout/Panel';
import PieChartWithNeedle from '../../../layout/components/PieChartWithNeedle';

const dialData = [
    { name: 'Weak', value: 20, color: '#ef4444' },
    { name: 'Watch', value: 20, color: '#f97316' },
    { name: 'Neutral', value: 20, color: '#facc15' },
    { name: 'Positive', value: 20, color: '#84cc16' },
    { name: 'Strong', value: 20, color: '#10b981' },
];

const dialValue = (score, min = -8, max = 8) => {
    const numericValue = Number(score ?? 0);
    return Math.max(0, Math.min(100, ((numericValue - min) / (max - min)) * 100));
};

const formatNumber = (value, decimals = 2) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue.toFixed(decimals) : "-";
};

const formatCompact = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) && numericValue > 0
        ? new Intl.NumberFormat('en', {
            notation: 'compact',
            maximumFractionDigits: 2,
        }).format(numericValue)
        : "-";
};

const formatMetricValue = (metric) => {
    if (!metric) return "-";
    const numericValue = Number(metric.value);
    if (!Number.isFinite(numericValue)) return "-";
    return metric.metric_key === 'market_cap' ? formatCompact(numericValue) : numericValue.toFixed(2);
};

const metricValue = (metricByKey, key, fallbackValue, formatter = formatNumber) => {
    const formatted = formatMetricValue(metricByKey[key]);
    return formatted !== "-" ? formatted : formatter(fallbackValue);
};

const metricScore = (metricByKey, key, fallbackScore) => {
    return metricByKey[key]?.score ?? fallbackScore;
};

const metricPeerValue = (metricByKey, key, field, fallbackValue) => {
    return metricByKey[key]?.[field] ?? fallbackValue;
};

const scoreText = (value) => {
    if (value === null || value === undefined) return "-";
    const numericValue = Number(value ?? 0);
    return `${numericValue > 0 ? "+" : ""}${numericValue.toFixed(0)}`;
};

const scoreTone = (value) => {
    if (value === null || value === undefined) return "border-slate-200 bg-slate-50 text-slate-500";
    const numericValue = Number(value ?? 0);
    if (numericValue > 1) return "border-emerald-200 bg-emerald-50 text-emerald-700";
    if (numericValue > 0) return "border-lime-200 bg-lime-50 text-lime-700";
    if (numericValue === 0) return "border-slate-200 bg-slate-50 text-slate-600";
    if (numericValue > -2) return "border-amber-200 bg-amber-50 text-amber-700";
    return "border-red-200 bg-red-50 text-red-700";
};

const scoreLabel = (value) => {
    if (value === null || value === undefined) return "Not scored";
    const numericValue = Number(value ?? 0);
    if (numericValue > 1) return "Strong";
    if (numericValue > 0) return "Positive";
    if (numericValue === 0) return "Neutral";
    if (numericValue > -2) return "Watch";
    return "Weak";
};

const Benchmark = ({ label, value }) => (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
        <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">{label}</div>
        <div className="mt-1 text-sm font-bold text-slate-800">{value === undefined || value === null ? "-" : formatNumber(value)}</div>
    </div>
);

const MetricInfoTooltip = ({ metric }) => (
    <div className="relative inline-flex">
        <button
            type="button"
            className="flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-xs font-bold text-slate-500 hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700"
            aria-label={`${metric.title} definition`}
        >
            i
        </button>
        <div className="pointer-events-none absolute right-0 top-8 z-20 hidden w-80 rounded-lg border border-slate-200 bg-white p-4 text-left shadow-xl group-hover/metric-info:block">
            <div className="space-y-3 text-xs leading-5 text-slate-600">
                <div>
                    <div className="font-bold uppercase tracking-[0.12em] text-slate-400">What is it?</div>
                    <div className="mt-1 font-semibold text-slate-800">{metric.what}</div>
                </div>
                <div>
                    <div className="font-bold uppercase tracking-[0.12em] text-slate-400">Why important?</div>
                    <div className="mt-1">{metric.why}</div>
                </div>
                <div>
                    <div className="font-bold uppercase tracking-[0.12em] text-slate-400">What it tells us</div>
                    <div className="mt-1">{metric.signal}</div>
                </div>
                <div className="rounded-md bg-emerald-50 px-3 py-2 font-bold text-emerald-800">
                    {metric.better}
                </div>
            </div>
        </div>
    </div>
);

const SummaryTile = ({ label, value, detail }) => (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
        <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">{label}</div>
        <div className="mt-2 text-2xl font-bold text-slate-950">{value}</div>
        <div className="mt-1 text-sm font-semibold text-slate-500">{detail}</div>
    </div>
);

const FinancialMetricCard = ({ metric }) => (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
                <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">{metric.group}</div>
                <div className="mt-1 flex items-center gap-2">
                    <h3 className="text-lg font-bold text-slate-950">{metric.title}</h3>
                    <div className="group/metric-info">
                        <MetricInfoTooltip metric={metric} />
                    </div>
                </div>
            </div>
            <div className={`rounded-full border px-3 py-1 text-sm font-bold ${scoreTone(metric.score)}`}>
                {scoreText(metric.score)}
            </div>
        </div>
        <div className="mt-4 flex items-end justify-between gap-4">
            <div>
                <div className="text-3xl font-bold text-slate-950">{metric.value}</div>
                <div className="mt-1 text-sm font-semibold text-slate-500">{metric.description}</div>
            </div>
            <div className={`rounded-md border px-2 py-1 text-xs font-bold uppercase tracking-[0.12em] ${scoreTone(metric.score)}`}>
                {scoreLabel(metric.score)}
            </div>
        </div>
        <div className="mt-4 grid gap-2 sm:grid-cols-3">
            <Benchmark label="Peer Low" value={metric.industryMin} />
            <Benchmark label="Peer Avg" value={metric.industryAverage} />
            <Benchmark label="Peer High" value={metric.industryMax} />
        </div>
    </div>
);

const SupportMetricCard = ({ metric }) => (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
        <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
                <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">{metric.group}</div>
                <div className="mt-1 flex items-center gap-2">
                    <h3 className="text-base font-bold text-slate-950">{metric.title}</h3>
                    <div className="group/metric-info">
                        <MetricInfoTooltip metric={metric} />
                    </div>
                </div>
            </div>
            {metric.score !== undefined && metric.score !== null ? (
                <div className={`rounded-full border px-3 py-1 text-sm font-bold ${scoreTone(metric.score)}`}>
                    {scoreText(metric.score)}
                </div>
            ) : null}
        </div>
        <div className="mt-3 text-2xl font-bold text-slate-950">{metric.value}</div>
        <div className="mt-1 text-sm font-semibold text-slate-500">{metric.description}</div>
    </div>
);

export const FinanceTab = ({ company, financialMetricsData = [] }) => {
    const metrics = Array.isArray(financialMetricsData)
        ? financialMetricsData
        : Object.values(financialMetricsData || {});

    const financialScore =
        Number(company.financial_metrics_score ?? (
            Number(company.financial_pe_score || 0) +
            Number(company.financial_ae_score || 0) +
            Number(company.financial_pv_score || 0) +
            Number(company.financial_recommendation_score || 0)
        ));

    const metricByKey = metrics.reduce((carry, metric) => {
        carry[metric.metric_key] = metric;
        return carry;
    }, {});

    const primaryMetrics = [
        {
            group: "Valuation",
            title: "P/E Ratio",
            description: "Price over earnings",
            what: "Share price divided by earnings per share.",
            why: "It is a compact view of how much investors are paying for each unit of reported earnings.",
            signal: "A high P/E can indicate rich valuation or high expected growth; a low P/E can indicate cheaper valuation or weaker expectations.",
            better: "Lower is usually better for the peer score.",
            value: metricValue(metricByKey, 'pe_ratio', company.pe_ratio),
            score: metricScore(metricByKey, 'pe_ratio', company.financial_pe_score),
            industryMin: metricPeerValue(metricByKey, 'pe_ratio', 'peer_low', company.industry?.pe_lower_value),
            industryMax: metricPeerValue(metricByKey, 'pe_ratio', 'peer_high', company.industry?.pe_upper_value),
            industryAverage: metricPeerValue(metricByKey, 'pe_ratio', 'peer_avg', company.industry?.pe_average_value),
        },
        {
            group: "Leverage",
            title: "A/E Ratio",
            description: "Assets over equity",
            what: "Total assets divided by shareholder equity.",
            why: "It shows how much of the balance sheet is supported by equity versus liabilities.",
            signal: "Higher A/E can indicate more leverage and greater balance-sheet sensitivity.",
            better: "Lower is usually better for the peer score.",
            value: metricValue(metricByKey, 'ae_ratio', company.ae_ratio),
            score: metricScore(metricByKey, 'ae_ratio', company.financial_ae_score),
            industryMin: metricPeerValue(metricByKey, 'ae_ratio', 'peer_low', company.industry?.ae_lower_value),
            industryMax: metricPeerValue(metricByKey, 'ae_ratio', 'peer_high', company.industry?.ae_upper_value),
            industryAverage: metricPeerValue(metricByKey, 'ae_ratio', 'peer_avg', company.industry?.ae_average_value),
        },
        {
            group: "Profitability",
            title: "P/V Ratio",
            description: "Profit over volume",
            what: "Gross profit divided by revenue.",
            why: "It captures profit intensity relative to the volume of business generated.",
            signal: "Higher P/V can indicate stronger gross profitability and pricing power.",
            better: "Higher is usually better for the peer score.",
            value: metricValue(metricByKey, 'pv_ratio', company.pv_ratio),
            score: metricScore(metricByKey, 'pv_ratio', company.financial_pv_score),
            industryMin: metricPeerValue(metricByKey, 'pv_ratio', 'peer_low', company.industry?.pv_lower_value),
            industryMax: metricPeerValue(metricByKey, 'pv_ratio', 'peer_high', company.industry?.pv_upper_value),
            industryAverage: metricPeerValue(metricByKey, 'pv_ratio', 'peer_avg', company.industry?.pv_average_value),
        },
        {
            group: "Market View",
            title: "Sentiment",
            description: "Recommendation signal",
            what: "A market confidence signal from analyst recommendations, news sentiment, or comparable opinion indicators.",
            why: "It brings an external market view into the financial comparison rather than relying only on accounting ratios.",
            signal: "Higher values indicate more constructive sentiment or recommendation support.",
            better: "Higher is usually better for the peer score.",
            value: metricValue(metricByKey, 'recommendation', company.recommendation),
            score: metricScore(metricByKey, 'recommendation', company.financial_recommendation_score),
            industryMin: metricPeerValue(metricByKey, 'recommendation', 'peer_low', company.industry?.recommendation_lower_value),
            industryMax: metricPeerValue(metricByKey, 'recommendation', 'peer_high', company.industry?.recommendation_upper_value),
            industryAverage: metricPeerValue(metricByKey, 'recommendation', 'peer_avg', company.industry?.recommendation_average_value),
        },
    ];

    const supportMetrics = [
        {
            group: "Leverage",
            title: "Debt-to-Equity",
            description: "Debt over equity",
            what: "Total debt divided by shareholder equity.",
            why: "It indicates how much financing risk sits on the balance sheet.",
            signal: "Higher debt-to-equity usually means more leverage and higher refinancing sensitivity.",
            better: "Lower is usually better for the peer score.",
            value: metricValue(metricByKey, 'debt_to_equity_ratio', company.debt_to_equity_ratio),
            score: metricScore(metricByKey, 'debt_to_equity_ratio', company.financial_debt_to_equity_score),
        },
        {
            group: "Profitability",
            title: "Dividend-to-Earnings",
            description: "Dividend per share over EPS",
            what: "Dividend per share divided by earnings per share.",
            why: "It shows how much of earnings are paid out rather than retained for reinvestment.",
            signal: "A very high ratio can suggest payout pressure; a moderate ratio can indicate income support.",
            better: "Balanced or lower payout pressure is usually better.",
            value: metricValue(metricByKey, 'dividend_to_earnings_ratio', company.dividend_to_earnings_ratio),
            score: metricScore(metricByKey, 'dividend_to_earnings_ratio', company.financial_dividend_to_earnings_score),
        },
    ];

    const availableMetrics = primaryMetrics.filter((metric) => metric.value !== "-").length;
    const latestPeriod = metrics.find((metric) => metric.period_end_date)?.period_end_date || company.last_financial_period_end;
    const marketCap = metricValue(metricByKey, 'market_cap', company.market_cap, formatCompact);

    return (
        <div className="space-y-4">
            <Panel className="border border-slate-200 shadow-sm">
                <div className="grid gap-5 lg:grid-cols-[220px_minmax(0,1fr)_auto] lg:items-center">
                    <div className="flex justify-center">
                        <PieChartWithNeedle data={dialData} width={200} height={160} value={dialValue(financialScore)} />
                    </div>
                    <div>
                        <p className="text-xs font-bold uppercase tracking-[0.16em] text-emerald-600">Financial view</p>
                        <h2 className="mt-2 text-2xl font-bold text-slate-950">Financial Score {scoreText(financialScore)}</h2>
                        <p className="mt-2 max-w-3xl text-sm font-medium leading-6 text-slate-500">
                            The headline score uses the master financial metrics: P/E, A/E, P/V, and sentiment, each pegged against the available peer group.
                        </p>
                    </div>
                    <div className={`w-fit rounded-full border px-4 py-2 text-sm font-bold ${scoreTone(financialScore)}`}>
                        {scoreLabel(financialScore)}
                    </div>
                </div>
                <div className="mt-5 grid gap-3 md:grid-cols-3">
                    <SummaryTile label="Primary Metrics" value={`${availableMetrics}/4`} detail="master financial fields populated" />
                    <SummaryTile label="Market Cap" value={marketCap} detail="equity market value" />
                    <SummaryTile label="Latest Period" value={latestPeriod || "-"} detail="last full quarter" />
                </div>
            </Panel>

            <div className="grid gap-4 xl:grid-cols-2">
                {primaryMetrics.map((metric) => (
                    <FinancialMetricCard key={metric.title} metric={metric} />
                ))}
            </div>

            <Panel className="border border-slate-200 shadow-sm">
                <div>
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-emerald-600">Support metrics</p>
                    <h3 className="mt-1 text-xl font-bold text-slate-950">Balance-sheet and dividend context</h3>
                </div>
                <div className="mt-4 grid gap-4 xl:grid-cols-2">
                    {supportMetrics.map((metric) => (
                        <SupportMetricCard key={metric.title} metric={metric} />
                    ))}
                </div>
            </Panel>
        </div>
    );
};
